import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { MapPin, Phone, Mail, Clock, MessageCircle, Send, Facebook, Linkedin } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import heroBackground from "@/assets/hero-background.jpg";

const formSchema = z.object({
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().optional(),
  company: z.string().optional(),
  service: z.string().min(1, "Please select a service"),
  message: z.string().min(10, "Message must be at least 10 characters"),
});

const Contact = () => {
  const { toast } = useToast();
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      company: "",
      service: "",
      message: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const formData = new FormData();
      formData.append('firstName', values.firstName);
      formData.append('lastName', values.lastName);
      formData.append('email', values.email);
      formData.append('phone', values.phone || '');
      formData.append('company', values.company || '');
      formData.append('service', values.service);
      formData.append('message', values.message);
      formData.append('_subject', `New Contact Form Submission from ${values.firstName} ${values.lastName}`);
      formData.append('_captcha', 'false');
      formData.append('_template', 'table');

      const response = await fetch('https://formsubmit.co/enquiry@wletec.com', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        toast({
          title: "Message sent successfully!",
          description: "We'll get back to you within 24 hours.",
        });
        form.reset();
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast({
        title: "Error sending message",
        description: "Please try again or contact us directly.",
        variant: "destructive",
      });
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Our Location",
      content: "7, Jalan Utarid U5/17,\nMahsing Integrated Industrial park,\nSeksyen U5, 40150 Shah Alam,\nSelangor Darul Ehsan. Malaysia",
      action: "View on Google Maps"
    },
    {
      icon: Phone,
      title: "Phone Number",
      content: "+60 11 3906 9019",
      action: "Call Now"
    },
    {
      icon: Mail,
      title: "Email Address", 
      content: "enquiry@wletec.com",
      action: "Send Email"
    },
    {
      icon: Clock,
      title: "Business Hours (UTC+8)",
      content: "Office:\nMon - Fri: 8:00 AM - 5:00 PM\nSaturday: 8:00 AM - 1:00 PM\n\nFactory:\nMon - Sat: 8:00 AM - 5:00 PM",
      action: null
    }
  ];

  const services = [
    "Tank Fabrication",
    "Mechanical Polishing", 
    "Electropolishing",
    "General Inquiry"
  ];

  return (
    <div className="min-h-screen bg-page-modern">
      <Helmet>
        <title>Contact WLE Technology | Get a Quote for Tank Fabrication</title>
        <meta name="description" content="Contact WLE Technology for custom tank fabrication quotes. Located in Shah Alam, Malaysia. Call +60 11 3906 9019 or email enquiry@wletec.com for immediate assistance." />
        <link rel="canonical" href="https://wletec.com/contact" />
        <meta property="og:title" content="Contact WLE Technology - Get a Quote" />
        <meta property="og:description" content="Get in touch for tank fabrication, electropolishing, and EPCC solutions." />
        <meta property="og:url" content="https://wletec.com/contact" />
        <meta property="og:type" content="website" />
        <meta name="keywords" content="contact WLE, tank fabrication quote, Shah Alam, Malaysia" />
      </Helmet>
      <Navigation />
      
      {/* Hero Section */}
      <section 
        className="py-24 relative"
        style={{
          backgroundImage: `url(${heroBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/75 via-slate-900/70 to-slate-900/65" />
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        <div className="container relative z-10">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-foreground mb-6">
              <span className="text-wle-blue">Contact</span> <span className="text-white">WLE</span>
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Ready to discuss your tank fabrication needs? Get in touch with our team for a consultation, 
              quote, or any questions about our services.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 bg-subtle-texture">
        <div className="container">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {contactInfo.map((info, index) => (
              <Card key={index} className="text-center hover:shadow-blue transition-all duration-300">
                <CardHeader>
                  <div className="mx-auto mb-4 p-4 rounded-full bg-wle-blue/10">
                    <info.icon className="h-6 w-6 text-wle-blue" />
                  </div>
                  <CardTitle className="text-lg">{info.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4 whitespace-pre-line">{info.content}</p>
                  {info.action && (
                    <Button variant="outline" size="sm">
                      {info.action}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-16">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Send Us a Message</CardTitle>
                <CardDescription>
                  Fill out the form below and we'll get back to you within 24 hours
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>First Name *</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter your first name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Last Name *</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter your last name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email Address *</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="your@email.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone Number</FormLabel>
                            <FormControl>
                              <Input placeholder="+60 12-345 6789" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <FormField
                      control={form.control}
                      name="company"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Company Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Your company name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="service"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Service Interest *</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a service" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {services.map((service) => (
                                <SelectItem key={service} value={service}>
                                  {service}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Message *</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Please describe your requirements, capacity needs, materials, timeline, etc."
                              className="min-h-[120px]"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <Button type="submit" className="w-full" size="lg" disabled={form.formState.isSubmitting}>
                      <Send className="mr-2 h-4 w-4" />
                      {form.formState.isSubmitting ? "Sending..." : "Send Message"}
                    </Button>
                    
                    <p className="text-sm text-muted-foreground text-center">
                      By submitting this form, you agree to our privacy policy and terms of service.
                    </p>
                  </form>
                </Form>
              </CardContent>
            </Card>

            {/* Quick Contact */}
            <div className="space-y-6">
              
              <Card className="bg-wle-blue text-white">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <MessageCircle className="h-8 w-8" />
                    <div>
                      <h3 className="text-xl font-bold">WhatsApp Direct</h3>
                      <p className="opacity-90">Get instant responses</p>
                    </div>
                  </div>
                  <p className="mb-6 opacity-90">
                    Need immediate assistance? Chat with our team directly on WhatsApp for quick quotes and technical questions.
                  </p>
                  <Button asChild variant="secondary" className="w-full">
                    <a href="https://wa.me/601139069019" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center">
                      <MessageCircle className="mr-2 h-4 w-4" />
                      Chat on WhatsApp
                    </a>
                  </Button>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-primary text-white">
                <CardContent className="p-8 text-center">
                  <h3 className="text-xl font-bold mb-4">Emergency Support</h3>
                  <p className="mb-4 opacity-90">
                    For urgent technical support or emergency repairs:
                  </p>
                  <p className="text-2xl font-bold mb-4">+60 11 3906 9019</p>
                  <p className="text-sm opacity-75">Available 24/7 for existing clients</p>
                </CardContent>
              </Card>
              
              {/* Social Media Card */}
              <Card>
                <CardContent className="p-8 text-center">
                  <h3 className="text-xl font-bold mb-4">Connect With Us</h3>
                  <p className="text-muted-foreground mb-6">
                    Follow our latest projects and industry insights
                  </p>
                  <div className="flex justify-center gap-4 mb-4">
                    <Button asChild variant="outline" size="sm">
                      <a 
                        href="https://www.facebook.com/WLEtech" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2"
                      >
                        <Facebook className="h-4 w-4" />
                        WLE Tank Fabrication
                      </a>
                    </Button>
                    <Button asChild variant="outline" size="sm">
                      <a 
                        href="https://www.linkedin.com/in/edwin-wong-wle/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2"
                      >
                        <Linkedin className="h-4 w-4" />
                        Director
                      </a>
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">Connect with our Director on LinkedIn</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-grid-pattern">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          <div className="grid lg:grid-cols-2 gap-6">
            {[
              {
                question: "What's your typical lead time for tank fabrication?",
                answer: "Lead times vary depending on size and complexity, typically 4-12 weeks for standard tanks. Custom designs may require additional time for engineering."
              },
              {
                question: "Do you provide installation services?",
                answer: "Yes, we offer complete EPCC services including installation, commissioning, and testing. Our team can handle both shop-fabricated and site-erected installations."
              },
              {
                question: "What materials do you work with?",
                answer: "We specialize in stainless steel (SS304, SS316L), duplex steel, carbon steel, and exotic materials like titanium and Hastelloy."
              },
              {
                question: "Can you handle custom designs?",
                answer: "Absolutely! We accept custom designs and drawings, or we can provide complete engineering services from concept to completion."
              },
              {
                question: "What quality certifications do you have?",
                answer: "We maintain quality management systems and can provide ASME, PED, and other certifications as required by your application."
              },
              {
                question: "Do you ship internationally?",
                answer: "Yes, we serve clients across Southeast Asia and internationally. We can arrange shipping and logistics for overseas projects."
              }
            ].map((faq, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle className="text-lg">{faq.question}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;